import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogo } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";

const inputCls = "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none ring-primary transition-shadow focus:ring-2";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-16 px-5 py-16 sm:px-8 md:grid-cols-12 md:py-24" data-testid={`${mode}-page`}>
      <div className="md:col-span-5">
        <p className="eyebrow">{isLogin ? "Welcome back" : "Create an account"}</p>
        <h1 className="mt-3 text-4xl font-medium leading-tight tracking-tight sm:text-5xl">{isLogin ? "Sign in to your shelf." : "Your orders, in one place."}</h1>
        <p className="mt-5 max-w-sm text-base leading-relaxed text-foreground/75">An account is how we know it's you — so your orders, invoices and tracking details stay in one place.</p>
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <form onSubmit={submit} className="rounded-3xl bg-white p-8 ring-1 ring-border/70">
          <button type="button" onClick={google} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-foreground/15 text-sm font-semibold transition-colors hover:bg-muted" data-testid="google-signin-button">
            <GoogleLogo weight="bold" size={18} /> Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or with email<span className="h-px flex-1 bg-border" /></div>
          <div className="space-y-4">
            {!isLogin && <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="signup-name-input" />}
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid={`${mode}-email-input`} />
            <input required type="password" minLength={6} placeholder={isLogin ? "Password" : "Password (min 6 characters)"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid={`${mode}-password-input`} />
          </div>
          {isLogin && <div className="mt-3 text-right"><Link to="/forgot-password" className="text-xs font-semibold text-muted-foreground hover:text-primary" data-testid="forgot-password-link">Forgot password?</Link></div>}
          {error && <p className="mt-4 rounded-xl bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} className="mt-6 h-12 w-full rounded-full bg-foreground text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid={`${mode}-submit-button`}>
            {busy ? "One moment…" : isLogin ? "Sign in" : "Create account"}
          </button>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={`${isLogin ? "/signup" : "/login"}?next=${encodeURIComponent(next)}`} className="font-semibold text-primary" data-testid="auth-switch-link">{isLogin ? "Create an account" : "Sign in"}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
